/**
 * EventRow Component
 *
 * Displays a calendar event using the UniversalRow slot system.
 * Maps CalendarEvent data to the appropriate slots.
 */

import React, { useMemo, useState } from 'react';
import { Calendar, MapPin, Users } from 'lucide-react';
import {
  UniversalRow,
  FastActions,
  RowBadge,
  MetaSeparator,
  PrimaryAction,
  EntityAvatar,
  type FastActionType,
  type StatusLevel,
} from '../common/base';
import { LinkManager } from '../../services/actions/LinkManager';
import { getAvailableActions } from '../common/registry/ActionRegistry';
import { useProjectsStore } from '@business-assistant/shared/stores';
import { useTranslation } from '../../hooks';
import type { CalendarEvent } from '@business-assistant/shared/services';

// =============================================================================
// TYPES
// =============================================================================

export interface EventRowProps {
  entity: CalendarEvent;
  onTitleClick?: (event: CalendarEvent) => void;
  onActionClick?: (actionType: string, eventId: string) => void;
  onAvatarClick?: (name: string, id?: string) => void;
}

// =============================================================================
// COMPONENT
// =============================================================================

export function EventRow({ entity: event, onTitleClick, onActionClick, onAvatarClick }: EventRowProps) {
  const { t, language } = useTranslation();
  const locale = language === 'it' ? 'it-IT' : 'en-US';

  // Primary action state (favorite)
  const [isFavorite, setIsFavorite] = useState(false);

  // Get project info if linked
  const project = useProjectsStore((state) =>
    event.projectId ? state.projects.find((p) => p.id === event.projectId) : undefined
  );

  // Prepare attendee avatars
  const attendeeAvatars = useMemo(() => {
    if (!event.attendees || event.attendees.length === 0) return [];
    return event.attendees.map((att) => ({
      id: att.email,
      name: att.name || att.email,
    }));
  }, [event.attendees]);

  // Left slot - Primary Action + Attendee Avatars
  const leftSlot = (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <PrimaryAction
        type="star"
        active={isFavorite}
        onChange={(favorite) => {
          setIsFavorite(favorite);
          onActionClick?.('favorite', event.id);
        }}
      />
      <EntityAvatar
        items={attendeeAvatars}
        type="person"
        size={24}
        maxVisible={3}
        onClick={onAvatarClick ? (item) => onAvatarClick(item.name, item.id) : undefined}
      />
    </div>
  );

  // Get linked action counts
  const linkedCounts = useMemo(() => {
    return LinkManager.getLinkedActionCounts(event.id);
  }, [event.id]);

  // Get available actions
  const availableActions = useMemo(() => {
    return getAvailableActions(event.id) as FastActionType[];
  }, [event.id]);

  // Determine temporal status
  const temporalStatus = useMemo(() => {
    const now = new Date();
    const today = now.toISOString().split('T')[0];
    const eventDate = event.startAt.split('T')[0];

    if (eventDate < today) return 'past';
    if (eventDate === today) return 'today';
    return 'future';
  }, [event.startAt]);

  // === SLOT: title ===
  const title = event.title;

  // === SLOT: subtitle (time info) ===
  const subtitle = useMemo(() => {
    if (event.allDay) return t('events.allDay');

    const date = new Date(event.startAt);
    const time = date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });

    if (event.endAt) {
      const duration = calculateDuration(event.startAt, event.endAt, t);
      return duration ? `${time} · ${duration}` : time;
    }
    return time;
  }, [event, t, locale]);

  // === SLOT: icon ===
  const icon = useMemo(() => {
    const color = event.color || getTemporalColor(temporalStatus);
    return <Calendar size={16} color={color} />;
  }, [event.color, temporalStatus]);

  // === SLOT: meta (location, attendees, project) ===
  const meta = useMemo(() => {
    const parts: React.ReactNode[] = [];

    // Location
    if (event.location) {
      parts.push(
        <span key="loc" style={{ display: 'inline-flex', alignItems: 'center' }}>
          <MapPin size={11} style={{ marginRight: '3px' }} />
          {event.location}
        </span>
      );
    }

    // Attendees count
    if (event.attendees && event.attendees.length > 0) {
      if (parts.length > 0) parts.push(<MetaSeparator key="sep1" />);
      parts.push(
        <span key="att" style={{ display: 'inline-flex', alignItems: 'center' }}>
          <Users size={11} style={{ marginRight: '3px' }} />
          {event.attendees.length}
        </span>
      );
    }

    // Project badge
    if (project) {
      if (parts.length > 0) parts.push(<MetaSeparator key="sep2" />);
      parts.push(<RowBadge key="proj" text={project.title} color="info" />);
    }

    // All day badge
    if (event.allDay) {
      if (parts.length > 0) parts.push(<MetaSeparator key="sep3" />);
      parts.push(<RowBadge key="allday" text={t('events.allDay')} color="#7c3aed" />);
    }

    return parts.length > 0 ? <>{parts}</> : null;
  }, [event, project, t]);

  // === SLOT: status (temporal indicator) ===
  const status: StatusLevel | undefined = useMemo(() => {
    if (temporalStatus === 'today') return 'warning';
    if (temporalStatus === 'past') return 'neutral';
    return undefined;
  }, [temporalStatus]);

  // === SLOT: done (past events appear faded) ===
  const done = temporalStatus === 'past';

  // === SLOT: rightSlot (fast actions) ===
  const rightSlot = availableActions.length > 0 ? (
    <FastActions
      actions={availableActions}
      counts={linkedCounts as Partial<Record<FastActionType, number>>}
      onAction={(action) => onActionClick?.(action, event.id)}
    />
  ) : null;

  return (
    <UniversalRow
      id={event.id}
      leftSlot={leftSlot}
      title={title}
      subtitle={subtitle}
      icon={icon}
      meta={meta}
      date={event.startAt}
      status={status}
      done={done}
      rightSlot={rightSlot}
      onTitleClick={() => onTitleClick?.(event)}
    />
  );
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

function calculateDuration(startAt: string, endAt: string, t: (key: string, options?: Record<string, unknown>) => string): string | null {
  const start = new Date(startAt);
  const end = new Date(endAt);
  const diffMs = end.getTime() - start.getTime();

  if (diffMs <= 0) return null;

  const diffMins = Math.round(diffMs / (1000 * 60));
  const hours = Math.floor(diffMins / 60);
  const mins = diffMins % 60;

  if (hours === 0) return t('common.minutesShort', { count: mins });
  if (mins === 0) return t('common.hoursShort', { count: hours });
  return t('common.hoursAndMinutes', { hours, minutes: mins });
}

function getTemporalColor(status: 'past' | 'today' | 'future'): string {
  switch (status) {
    case 'past': return '#9ca3af';
    case 'today': return '#8b5cf6';
    case 'future': return '#3b82f6';
    default: return '#3b82f6';
  }
}
